import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { motion, AnimatePresence } from 'framer-motion';
import { BarChart3, ListTree, History, RefreshCw, ChevronDown, Copy, ListChecks, X } from 'lucide-react';
import {
  getAdminNotificationsAggregateStatistics, getAdminNotificationStatistics,
  getAdminNotificationDeliveries, getAdminNotificationAudit,
  NOTIFICATION_CATEGORY_LABELS,
} from '../../composables/notificationAPI';
import AdminNotificationList from './AdminNotificationList';

const inputCls = 'w-full rounded-lg px-3 py-2 text-sm';
const inputStyle = { backgroundColor: 'var(--bg-item)', color: 'var(--text-color)' } as const;
const btnDisabledCls = 'disabled:opacity-40 disabled:cursor-not-allowed';

// ---------- พจนานุกรมแปลศัพท์ระบบ → ไทย (อ้างอิง notification-platform.md 3.2 / 19.2) ----------

// _id ของ aggregate stats เป็น "channel.metric" เช่น "inApp.read" — เผื่อ backend เปลี่ยน casing เลยรองรับทั้งสองแบบ
const CHANNEL_LABELS: Record<string, string> = {
  inApp: 'ในแอป', IN_APP: 'ในแอป',
  webPush: 'พุช', WEB_PUSH: 'พุช',
  telegram: 'Telegram', TELEGRAM: 'Telegram',
  audience: 'ผู้รับเป้าหมาย',
};

// คำศัพท์ที่รู้จักแน่ ๆ จากเอกสาร แปลตรงตัว — ถ้าไม่รู้จักค่อย fallback เป็นตัวพิมพ์ใหญ่ตัวแรกแบบเดิม
const TOKEN_LABELS: Record<string, string> = {
  // การกระทำของผู้ใช้ (9. User Notification APIs)
  seen: 'เห็นแล้ว', read: 'อ่านแล้ว', unread: 'ยังไม่อ่าน', click: 'คลิก', clicked: 'คลิกแล้ว',
  acknowledge: 'รับทราบ', acknowledged: 'รับทราบแล้ว', ack: 'รับทราบ', dismiss: 'ปิดทิ้ง', dismissed: 'ปิดทิ้งแล้ว',
  // สถานะการส่ง (3.2 Delivery statuses)
  sent: 'ส่งสำเร็จ', failed: 'ส่งไม่สำเร็จ', suppressed: 'ถูกระงับ', queued: 'เข้าคิว', delivered: 'ส่งถึงแล้ว',
  pending: 'รอดำเนินการ', sending: 'กำลังส่ง', expired: 'หมดอายุ', retry: 'ลองใหม่', retries: 'จำนวนครั้งที่ลองใหม่',
  publish: 'เผยแพร่', published: 'เผยแพร่แล้ว',
  // ยอดรวม/สิทธิ์
  total: 'ทั้งหมด', eligible: 'มีสิทธิ์รับ', ineligible: 'ไม่มีสิทธิ์รับ', potential: 'ผู้รับที่เป็นไปได้',
  // เหตุผลที่ถูกระงับ (19.2 Internal suppression / delivery reasons)
  userinactive: 'ผู้ใช้ไม่ active', notEligible: 'ไม่มีสิทธิ์รับ', channelDisabled: 'ช่องทางปิดอยู่',
  userPreference: 'ผู้ใช้ปิดรับช่องทางนี้เอง', quietHours: 'ช่วงเวลางดรบกวน',
  telegramNotLinked: 'ยังไม่ผูก Telegram', telegramUnavailable: 'Telegram ใช้งานไม่ได้',
  noPushSubscription: 'ไม่มีอุปกรณ์ที่เปิดรับ Push', rateLimit: 'ส่งถี่เกินกำหนด',
  telegramBlocked: 'ผู้ใช้บล็อกบอท', invalidSubscription: 'อุปกรณ์ใช้งานไม่ได้แล้ว',
  noValidDevices: 'ไม่มีอุปกรณ์ที่ใช้งานได้', deliveryOutcomeUnknown: 'ไม่ทราบผลส่งแน่ชัด',
};

function translateToken(raw: string): string | null {
  const key = raw.replace(/[\s._-]/g, '').toLowerCase();
  const hit = Object.entries(TOKEN_LABELS).find(([k]) => k.replace(/[\s._-]/g, '').toLowerCase() === key);
  return hit ? hit[1] : null;
}

// แตกคำผสม (camelCase / SNAKE_CASE / dot.path) เป็น token ย่อย แล้วแปลทีละตัว ต่อด้วย " · "
// ตัวที่ไม่รู้จักจะ fallback เป็นคำเดิมแต่ทำให้อ่านง่ายขึ้น (Title Case + เว้นวรรค) แทนที่จะโชว์รหัสดิบ
function prettifyKey(key: string): string {
  const tokens = key.split(/[._]/).flatMap((seg) => seg.split(/(?=[A-Z])/)).map((t) => t.trim()).filter(Boolean);
  const translated = tokens.map((t) => translateToken(t) ?? (t.charAt(0).toUpperCase() + t.slice(1).toLowerCase()));
  return translated.filter((t, i) => t !== translated[i - 1]).join(' · ');
}

const DELIVERY_STATUS_LABELS: Record<string, string> = {
  SENT: 'ส่งสำเร็จ', FAILED: 'ส่งไม่สำเร็จ', SUPPRESSED: 'ถูกระงับ', EXPIRED: 'หมดอายุ',
  QUEUED: 'รอคิว', SENDING: 'กำลังส่ง', PENDING: 'รอดำเนินการ',
};
const DELIVERY_STATUS_STYLE: Record<string, { bg: string; color: string }> = {
  SENT: { bg: 'rgba(34,197,94,0.15)', color: 'var(--green-text-color)' },
  FAILED: { bg: 'rgba(239,68,68,0.15)', color: 'var(--red-text-color)' },
  SUPPRESSED: { bg: 'rgba(239,68,68,0.12)', color: 'var(--red-text-color)' },
  EXPIRED: { bg: 'rgba(148,163,184,0.18)', color: 'var(--secondary-text-color)' },
  QUEUED: { bg: 'rgba(234,179,8,0.15)', color: 'var(--yellow-text-color)' },
  SENDING: { bg: 'rgba(234,179,8,0.15)', color: 'var(--yellow-text-color)' },
  PENDING: { bg: 'rgba(148,163,184,0.15)', color: 'var(--secondary-text-color)' },
};

const AUDIT_ACTION_LABELS: Record<string, string> = {
  CREATE: 'สร้างดราฟต์', UPDATE: 'แก้ไข', PUBLISH: 'เผยแพร่', SCHEDULE: 'ตั้งเวลา',
  CANCEL: 'ยกเลิก', EXPIRE: 'ปิดประกาศ', TEST: 'ส่งทดสอบ',
};
const AUDIT_ACTION_COLOR: Record<string, string> = {
  PUBLISH: 'var(--green-text-color)',
  CANCEL: 'var(--red-text-color)',
  EXPIRE: 'var(--red-text-color)',
  SCHEDULE: 'var(--yellow-text-color)',
  CREATE: 'var(--secondary-text-color)',
  UPDATE: 'var(--secondary-text-color)',
  TEST: 'var(--secondary-text-color)',
};

// ---------- ตัวช่วยแสดงผลทั่วไป ----------

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const style = DELIVERY_STATUS_STYLE[status] || { bg: 'rgba(148,163,184,0.15)', color: 'var(--secondary-text-color)' };
  return (
    <span className="inline-block text-[10px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap" style={{ backgroundColor: style.bg, color: style.color }}>
      {DELIVERY_STATUS_LABELS[status] || status}
    </span>
  );
};

const ChannelBadge: React.FC<{ channel: string }> = ({ channel }) => (
  <span className="inline-block text-[10px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap" style={{ backgroundColor: 'var(--bg-navbar, #e5e7eb)', color: 'var(--secondary-text-color)' }}>
    {CHANNEL_LABELS[channel] || prettifyKey(channel)}
  </span>
);

const CategoryBadge: React.FC<{ category: string }> = ({ category }) => (
  <span className="inline-block text-[10px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap" style={{ backgroundColor: 'var(--bg-navbar, #e5e7eb)', color: 'var(--secondary-text-color)' }}>
    {(NOTIFICATION_CATEGORY_LABELS as Record<string, string>)[category] || category}
  </span>
);

function formatDate(iso?: string): string {
  if (!iso) return '-';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('th-TH', { day: 'numeric', month: 'short', year: '2-digit', hour: '2-digit', minute: '2-digit' });
}

function copyToClipboard(text: string) {
  navigator.clipboard?.writeText(text).then(
    () => toast.success('คัดลอกแล้ว'),
    () => toast.error('คัดลอกไม่สำเร็จ'),
  );
}

const CopyableId: React.FC<{ value: string; short?: number }> = ({ value, short = 8 }) => (
  <button
    type="button"
    title={value}
    onClick={(e) => { e.stopPropagation(); copyToClipboard(value); }}
    className="inline-flex items-center gap-1 text-[11px] font-mono opacity-70 hover:opacity-100 transition-opacity"
  >
    {value.slice(0, short)}… <Copy size={10} />
  </button>
);

// แถว label:value ทั่วไปสำหรับ object ที่ยังไม่รู้ schema ล่วงหน้าแน่ชัด (เช่น counters ย่อยของแต่ละ channel)
const KeyValueRows: React.FC<{ data: Record<string, any> }> = ({ data }) => {
  const entries = Object.entries(data || {});
  if (entries.length === 0) return <p className="text-xs opacity-50">ไม่มีข้อมูล</p>;
  return (
    <div className="flex flex-col gap-1">
      {entries.map(([k, v]) => (
        <div key={k} className="flex items-center justify-between gap-2 text-xs">
          <span className="opacity-70">{prettifyKey(k)}</span>
          <span className="font-mono font-semibold">{typeof v === 'object' && v !== null ? JSON.stringify(v) : String(v)}</span>
        </div>
      ))}
    </div>
  );
};

// ---------- Aggregate stats: จัด [{_id:"inApp.read", total:123}] ให้เป็นการ์ดต่อ channel ----------

const AggregateStatsGrid: React.FC<{ rows: { _id: string; total: number }[] }> = ({ rows }) => {
  if (rows.length === 0) return <p className="text-sm opacity-60">ยังไม่มีสถิติ</p>;

  const groups: Record<string, { metric: string; total: number }[]> = {};
  rows.forEach((r) => {
    const dotIndex = r._id.indexOf('.');
    const channel = dotIndex === -1 ? 'อื่นๆ' : r._id.slice(0, dotIndex);
    const metric = dotIndex === -1 ? r._id : r._id.slice(dotIndex + 1);
    (groups[channel] ??= []).push({ metric, total: r.total });
  });

  let tileIndex = 0;
  return (
    <div className="grid sm:grid-cols-2 gap-3">
      {Object.entries(groups).map(([channel, metrics], groupIdx) => (
        <motion.div
          key={channel}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: groupIdx * 0.05, duration: 0.25, ease: 'easeOut' }}
          className="rounded-xl p-3"
          style={{ backgroundColor: 'var(--bg-item)' }}
        >
          <div className="text-xs font-bold mb-2 opacity-80">{CHANNEL_LABELS[channel] || prettifyKey(channel)}</div>
          <div className="grid grid-cols-2 gap-2">
            {metrics.map((m) => {
              const idx = tileIndex++;
              return (
                <motion.div
                  key={m.metric}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: idx * 0.03, duration: 0.2, ease: 'easeOut' }}
                  className="rounded-lg px-2.5 py-2"
                  style={{ backgroundColor: 'var(--component-bg-color)' }}
                >
                  <div className="text-lg font-black leading-none">{m.total.toLocaleString('th-TH')}</div>
                  <div className="text-[10px] opacity-60 mt-1">{prettifyKey(m.metric)}</div>
                </motion.div>
              );
            })}
          </div>
        </motion.div>
      ))}
    </div>
  );
};

// ---------- สถิติของรายการเดียว: counters แยกตาม channel/audience ----------

const NotificationCounters: React.FC<{ data: any }> = ({ data }) => {
  const counters = data?.counters;
  if (!counters || typeof counters !== 'object') return <p className="text-sm opacity-60">ไม่มีข้อมูลตัวนับ</p>;
  return (
    <div className="flex flex-col gap-3">
      {data.notificationId && (
        <div className="flex items-center gap-2 text-xs opacity-70">
          {data.category && <CategoryBadge category={data.category} />}
          <span className="font-mono truncate">{data.notificationId}</span>
        </div>
      )}
      <div className="grid sm:grid-cols-2 gap-3">
        {Object.entries(counters).map(([group, values], idx) => (
          <motion.div
            key={group}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: idx * 0.05, duration: 0.2, ease: 'easeOut' }}
            className="rounded-xl p-3"
            style={{ backgroundColor: 'var(--bg-item)' }}
          >
            <div className="text-xs font-bold mb-2 opacity-80">{CHANNEL_LABELS[group] || prettifyKey(group)}</div>
            <KeyValueRows data={(values as Record<string, any>) || {}} />
          </motion.div>
        ))}
      </div>
    </div>
  );
};

// ---------- Delivery journal: รายการพับ/กางดูรายละเอียด พร้อม badge สถานะ ----------

const DeliveryTable: React.FC<{ rows: any[] }> = ({ rows }) => {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  if (rows.length === 0) return <p className="text-sm opacity-60">ยังไม่มีการส่ง</p>;
  return (
    <div className="flex flex-col gap-1.5">
      {rows.map((row, idx) => {
        const isOpen = expandedId === row.id;
        return (
          <motion.div
            key={row.id}
            layout
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: Math.min(idx, 15) * 0.02, duration: 0.2, ease: 'easeOut' }}
            className="rounded-lg overflow-hidden"
            style={{ backgroundColor: 'var(--bg-item)' }}
          >
            <button
              type="button"
              onClick={() => setExpandedId(isOpen ? null : row.id)}
              className="w-full flex items-center gap-2 px-3 py-2 text-left"
            >
              <motion.span animate={{ rotate: isOpen ? 0 : -90 }} transition={{ duration: 0.15 }} className="shrink-0 opacity-60 flex">
                <ChevronDown size={13} />
              </motion.span>
              {row.channel && <ChannelBadge channel={row.channel} />}
              {row.status && <StatusBadge status={row.status} />}
              <span className="text-xs opacity-70 truncate flex-1">{row.userId || '-'}</span>
              {typeof row.attempts === 'number' && <span className="text-[11px] opacity-50 shrink-0">ลอง {row.attempts} ครั้ง</span>}
            </button>
            <AnimatePresence initial={false}>
              {isOpen && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2, ease: 'easeOut' }}
                  className="overflow-hidden"
                >
                  <div className="px-3 pb-3 flex flex-col gap-2 text-xs" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center gap-2 opacity-70">
                      <span>รหัสรายการ:</span> <CopyableId value={row.id} short={16} />
                    </div>
                    {row.userId && (
                      <div className="flex items-center gap-2 opacity-70">
                        <span>ผู้ใช้:</span> <CopyableId value={row.userId} short={16} />
                      </div>
                    )}
                    {row.lastError && (
                      <div>
                        <span className="opacity-70">ข้อผิดพลาดล่าสุด:</span>{' '}
                        <span className="font-semibold" style={{ color: 'var(--red-text-color)' }}>
                          {prettifyKey(row.lastError)} <span className="font-mono opacity-50">({row.lastError})</span>
                        </span>
                      </div>
                    )}
                    {row.suppressionReason && (
                      <div>
                        <span className="opacity-70">เหตุผลที่ถูกระงับ:</span>{' '}
                        <span className="font-semibold">
                          {prettifyKey(row.suppressionReason)} <span className="font-mono opacity-50">({row.suppressionReason})</span>
                        </span>
                      </div>
                    )}
                    {row.nextAttemptAt && (
                      <div><span className="opacity-70">จะลองใหม่:</span> <span className="font-mono">{formatDate(row.nextAttemptAt)}</span></div>
                    )}
                    {Array.isArray(row.targets) && row.targets.length > 0 && (
                      <div>
                        <div className="opacity-70 mb-1">ปลายทาง ({row.targets.length})</div>
                        <pre className="text-[10px] whitespace-pre-wrap break-all rounded-lg p-2" style={{ backgroundColor: 'var(--component-bg-color)' }}>
                          {JSON.stringify(row.targets, null, 2)}
                        </pre>
                      </div>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        );
      })}
    </div>
  );
};

// ---------- Audit: timeline แนวตั้ง ----------

const AuditTimeline: React.FC<{ rows: any[] }> = ({ rows }) => {
  if (rows.length === 0) return <p className="text-sm opacity-60">ยังไม่มีประวัติ</p>;
  return (
    <div className="flex flex-col">
      {rows.map((row, i) => (
        <motion.div
          key={i}
          initial={{ opacity: 0, x: -8 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: Math.min(i, 15) * 0.03, duration: 0.2, ease: 'easeOut' }}
          className="flex gap-3 relative pb-4 last:pb-0"
        >
          <div className="flex flex-col items-center shrink-0">
            <span className="w-2.5 h-2.5 rounded-full mt-1" style={{ backgroundColor: AUDIT_ACTION_COLOR[row.action] || 'var(--secondary-text-color)' }} />
            {i < rows.length - 1 && <span className="w-px flex-1 mt-1" style={{ backgroundColor: 'var(--bg-item)' }} />}
          </div>
          <div className="min-w-0 flex-1 -mt-0.5">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs font-bold" style={{ color: AUDIT_ACTION_COLOR[row.action] || 'var(--text-color)' }}>
                {AUDIT_ACTION_LABELS[row.action] || prettifyKey(row.action || '')}
              </span>
              <span className="text-[11px] opacity-50" title={row.createdAt}>{formatDate(row.createdAt)}</span>
            </div>
            <div className="text-xs opacity-70 truncate">โดย {row.actor || 'ระบบ'}</div>
            {row.entityId && <div className="text-[10px] opacity-40 font-mono truncate">{row.entityId}</div>}
          </div>
        </motion.div>
      ))}
    </div>
  );
};

// ---------- หน้าหลัก ----------

type ResultView = { kind: 'stats'; data: any } | { kind: 'deliveries'; data: any[] } | { kind: 'audit'; data: any[] } | null;

const AdminNotificationStats: React.FC<{ initialId: string }> = ({ initialId }) => {
  const [aggregateRows, setAggregateRows] = useState<{ _id: string; total: number }[] | null>(null);
  const [loadingAggregate, setLoadingAggregate] = useState(false);
  const [id, setId] = useState(initialId);
  const [result, setResult] = useState<ResultView>(null);
  const [deliveryCursor, setDeliveryCursor] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  // แยกจาก result.kind เพราะต้องติดสีปุ่มทันทีตอนกด/ตอนเลือกจากลิสต์ ไม่ใช่รอ API ตอบกลับก่อนค่อยติดสี
  const [activeTab, setActiveTab] = useState<'stats' | 'deliveries' | 'audit' | null>(null);

  useEffect(() => { setId(initialId); setResult(null); setDeliveryCursor(null); setActiveTab(null); }, [initialId]);

  const loadAggregate = async () => {
    setLoadingAggregate(true);
    try {
      const data = await getAdminNotificationsAggregateStatistics();
      setAggregateRows(Array.isArray(data) ? data : []);
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setLoadingAggregate(false);
    }
  };
  useEffect(() => { void loadAggregate(); }, []);

  // ทุก load* รับ id override ได้ เพื่อให้เรียกต่อจากการเลือกในลิสต์ได้ทันที โดยไม่ต้องรอ state id
  // อัปเดตก่อน (setId เป็น async — เรียกต่อเนื่องในฟังก์ชันเดียวกันจะยังเห็น id เก่าอยู่)
  const loadStats = async (overrideId?: string) => {
    const nid = (overrideId ?? id).trim();
    if (!nid) { toast.error('ระบุ Notification ID ก่อน'); return; }
    setActiveTab('stats');
    setBusy(true);
    try { setResult({ kind: 'stats', data: await getAdminNotificationStatistics(nid) }); }
    catch (err: any) { toast.error(err.message); }
    finally { setBusy(false); }
  };

  const loadDeliveries = async (more: boolean, overrideId?: string) => {
    const nid = (overrideId ?? id).trim();
    if (!nid) { toast.error('ระบุ Notification ID ก่อน'); return; }
    setActiveTab('deliveries');
    setBusy(true);
    try {
      const data = await getAdminNotificationDeliveries(nid, more ? deliveryCursor : null);
      setDeliveryCursor(data.length < 100 ? null : data.at(-1)?.id ?? null);
      setResult((prev) => ({
        kind: 'deliveries',
        data: more && prev?.kind === 'deliveries' ? [...prev.data, ...data] : data,
      }));
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  const loadAudit = async (overrideId?: string) => {
    const nid = (overrideId ?? id).trim();
    if (!nid) { toast.error('ระบุ Notification ID ก่อน'); return; }
    setActiveTab('audit');
    setBusy(true);
    try {
      const data = await getAdminNotificationAudit(nid);
      setResult({ kind: 'audit', data: Array.isArray(data) ? data : [] });
    } catch (err: any) { toast.error(err.message); }
    finally { setBusy(false); }
  };

  // เลือกรายการจากลิสต์ประกาศ (การ์ด/ตัวกรองเดียวกับแท็บ "รายการประกาศ") แทนต้องไปคัดลอก ID มาวางเอง
  // เลือกแล้วโหลด "สถิติรายการ" ให้เลยทันที ไม่ต้องกดซ้ำอีกครั้ง
  const handlePickFromList = (pickedId: string) => {
    setId(pickedId);
    setDeliveryCursor(null);
    setPickerOpen(false);
    void loadStats(pickedId);
  };

  const resultLabel = activeTab === 'stats' ? 'สถิติรายการ' : activeTab === 'deliveries' ? 'ผลส่ง' : activeTab === 'audit' ? 'ประวัติการดำเนินการ' : '';
  const tabBtnStyle = (tab: 'stats' | 'deliveries' | 'audit') =>
    activeTab === tab
      ? { backgroundColor: 'var(--yellow-text-color)', color: '#000' }
      : { backgroundColor: 'var(--bg-item)' };

  return (
    <>
    <div className="grid lg:grid-cols-2 gap-5">
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25 }}
        className="rounded-2xl p-5"
        style={{ backgroundColor: 'var(--component-bg-color)' }}
      >
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-bold flex items-center gap-2"><BarChart3 size={16} /> สถิติรวมทุกประกาศ <span className="text-xs font-normal opacity-50">(ไม่รวมรายการทดสอบ)</span></h3>
          <button onClick={loadAggregate} disabled={loadingAggregate} className="p-1.5 rounded-lg" style={{ backgroundColor: 'var(--bg-item)' }}>
            <motion.span animate={{ rotate: loadingAggregate ? 360 : 0 }} transition={{ duration: 0.6, ease: 'linear', repeat: loadingAggregate ? Infinity : 0 }} className="flex">
              <RefreshCw size={14} />
            </motion.span>
          </button>
        </div>
        {aggregateRows === null ? (
          <p className="text-sm opacity-60">กำลังโหลด...</p>
        ) : (
          <AggregateStatsGrid rows={aggregateRows} />
        )}
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25, delay: 0.05 }}
        className="rounded-2xl p-5 flex flex-col gap-3"
        style={{ backgroundColor: 'var(--component-bg-color)' }}
      >
        <div className="flex items-center justify-between gap-2">
          <h3 className="font-bold">ตรวจสอบรายการเดียว</h3>
          <button
            onClick={() => setPickerOpen(true)}
            className="flex items-center gap-1 text-xs font-semibold px-2.5 py-1.5 rounded-lg transition-transform active:scale-95"
            style={{ backgroundColor: 'var(--bg-item)' }}
          >
            <ListChecks size={13} /> เลือกจากรายการประกาศ
          </button>
        </div>
        <input
          className={inputCls}
          style={inputStyle}
          placeholder="วาง Notification ID ที่นี่ หรือกด 'เลือกจากรายการประกาศ' ด้านบน"
          value={id}
          onChange={(e) => { setId(e.target.value); setResult(null); setDeliveryCursor(null); setActiveTab(null); }}
        />
        <div className="flex flex-wrap gap-2">
          <button disabled={busy || !id.trim()} title={!id.trim() ? 'ระบุ Notification ID ก่อน' : undefined} onClick={() => loadStats()} className={'flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all active:scale-95 ' + btnDisabledCls} style={tabBtnStyle('stats')}><BarChart3 size={13} /> สถิติรายการ</button>
          <button disabled={busy || !id.trim()} title={!id.trim() ? 'ระบุ Notification ID ก่อน' : undefined} onClick={() => loadDeliveries(false)} className={'flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all active:scale-95 ' + btnDisabledCls} style={tabBtnStyle('deliveries')}><ListTree size={13} /> ผลส่ง</button>
          <button disabled={busy || !id.trim()} title={!id.trim() ? 'ระบุ Notification ID ก่อน' : undefined} onClick={() => loadAudit()} className={'flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all active:scale-95 ' + btnDisabledCls} style={tabBtnStyle('audit')}><History size={13} /> ประวัติการดำเนินการ</button>
        </div>

        <div className="min-h-[120px] max-h-[420px] overflow-y-auto">
          <AnimatePresence mode="wait">
            {result === null ? (
              <motion.p key="empty" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="text-sm opacity-60">
                เลือกดูสถิติ / ผลส่ง / ประวัติ ของรายการด้านบนก่อน
              </motion.p>
            ) : busy ? (
              <motion.p key="loading" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="text-sm opacity-60">
                กำลังโหลด{resultLabel}...
              </motion.p>
            ) : (
              <motion.div key={result.kind} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.18 }}>
                {result.kind === 'stats' && <NotificationCounters data={result.data} />}
                {result.kind === 'deliveries' && (
                  <>
                    <DeliveryTable rows={result.data} />
                    {deliveryCursor && (
                      <button disabled={busy} onClick={() => loadDeliveries(true)} className={'w-full mt-2 px-3 py-1.5 rounded-lg text-xs font-semibold ' + btnDisabledCls} style={{ backgroundColor: 'var(--bg-item)' }}>
                        โหลดผลส่งหน้าถัดไป
                      </button>
                    )}
                  </>
                )}
                {result.kind === 'audit' && <AuditTimeline rows={result.data} />}
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <p className="text-[11px] opacity-60 pt-2 border-t" style={{ borderColor: 'var(--bg-item)' }}>
          "ส่งสำเร็จ" หมายถึงผู้ให้บริการ (Push/Telegram) รับข้อความไปแล้วเท่านั้น ยังไม่ได้แปลว่าผู้ใช้เปิดอ่าน ·
          ตัวเลขในสถิติรวมเป็นยอดสะสมตลอดกาล ไม่รีเซ็ตรายวัน · ถ้าเจอ "ไม่ทราบผลส่งแน่ชัด" ห้ามส่งซ้ำเองแบบไม่ตรวจสอบก่อน
          เพราะอาจส่งซ้ำโดยไม่ตั้งใจ
        </p>
      </motion.div>
    </div>

    <AnimatePresence>
      {pickerOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
          className="fixed inset-0 z-[60] flex items-center justify-center p-4"
          style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}
          onClick={() => setPickerOpen(false)}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.97, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.97, y: 10 }}
            transition={{ duration: 0.18, ease: 'easeOut' }}
            // สูงไม่เกินจอ แล้วให้รายการเลื่อนข้างใน — เดิมกล่องสูงกว่าจอ + จัดกึ่งกลาง ทำให้ส่วนบนโดนตัดเลื่อนไปดูไม่ได้
            // --bg-color เป็น gradient ใส่ใน backgroundColor ไม่ได้ (พื้นเลยโปร่งใส) ต้องใช้ background
            className="w-full max-w-5xl max-h-[calc(100vh-2rem)] flex flex-col rounded-2xl p-5"
            style={{ background: 'var(--bg-color, var(--component-bg-color))' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-lg">เลือกประกาศเพื่อดูสถิติ</h3>
              <button onClick={() => setPickerOpen(false)} className="p-1.5 rounded-lg" style={{ backgroundColor: 'var(--bg-item)' }}>
                <X size={16} />
              </button>
            </div>
            <div className="min-h-0 overflow-y-auto -mx-1 px-1">
              <AdminNotificationList onSelect={handlePickFromList} />
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
    </>
  );
};

export default AdminNotificationStats;
